import { PropTypeOptions } from "../budget-review/BudgetReview.constant";

export const PropTypeOptionsMap: Record<string, string> = {
  UNIT: PropTypeOptions.unit,
  CONSOLIDATED: PropTypeOptions.consolidated
};

type BudgetLoanResponse = {
  budgetDocumentInfoList?: Array<{
    budgetStatus?: string;
    selectionMode?: string;
    propertyId?: string | number;
  }>;
};

export const getBudgetReviewPropType = (
  budgetData?: BudgetLoanResponse | null,
  propertyId?: string | number
): string | null => {
  const list = budgetData?.budgetDocumentInfoList;
  if (!list?.length) return null;

  if (propertyId != null && propertyId !== "") {
    const propertyDoc = list.find((doc) => doc.propertyId == propertyId);
    if (propertyDoc?.selectionMode) {
      return PropTypeOptionsMap[propertyDoc.selectionMode] ?? null;
    }
  }

  const activeDoc =
    list.find((doc) => doc.budgetStatus === "ACTIVE") ?? list[0];
  return PropTypeOptionsMap[activeDoc?.selectionMode ?? ""] ?? null;
};

const toNumericValueOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const firstFiniteAmount = (...values: unknown[]): number | null =>
  values.reduce<number | null>((found, value) => {
    if (found !== null) {
      return found;
    }
    return toNumericValueOrNull(value);
  }, null);

const getInspectionInfoForDraw = (row: any) => {
  const inspectionInfo =
    row?.inspectionInfo ?? row?.budgetLineItem?.inspectionInfo;
  if (!inspectionInfo || typeof inspectionInfo !== "object") {
    return null;
  }
  const drawId = row?.drawId ?? row?.budgetLineItem?.drawId;
  if (drawId == null) {
    return null;
  }
  const drawKey = String(drawId);
  return inspectionInfo[drawKey] ?? null;
};

export const getInspectionReportDrawAmount = (row: any): number | null => {
  const nested = row?.budgetLineItem;
  const inspectionInfo = getInspectionInfoForDraw(row);
  return firstFiniteAmount(
    nested?.inspectionReportDrawAmount,
    row?.inspectionReportDrawAmount,
    nested?.inspectionRequestedDrawAmount,
    row?.inspectionRequestedDrawAmount,
    row?.requestedAmount,
    nested?.requestedAmount,
    inspectionInfo?.requestedAmount
  );
};

// LOS formatBudget copies inspection/borrower amounts onto budgetLineItem so
// the reconcile SDK can snap Approved Draw Amount. TC was passing the API
// payload through unchanged, which left Gutters/Downspouts at $4,000.01.
export const formatBudget = (budgetListRes: any[] = []): any[] =>
  budgetListRes.map((row) => {
    const inspectionReportDrawAmount = getInspectionReportDrawAmount(row);
    const existingLineItem =
      row?.budgetLineItem && typeof row.budgetLineItem === "object"
        ? row.budgetLineItem
        : {};
    return {
      ...row,
      reconciledItemsId: row.reconciledItemsId ?? row.drawBudgetLineItemId,
      budgetLineItem: {
        ...existingLineItem,
        budget: existingLineItem.budget ?? row.budget,
        financedBudget: existingLineItem.financedBudget ?? row.financedBudget,
        item: existingLineItem.item ?? row.item,
        inspectionReportDrawAmount,
        borrowerRequestedDrawAmount:
          firstFiniteAmount(
            row.borrowerRequestedDrawAmount,
            existingLineItem.borrowerRequestedDrawAmount
          ),
        requestedDrawAmount:
          firstFiniteAmount(
            existingLineItem.requestedDrawAmount,
            row.requestedAmount,
            inspectionReportDrawAmount
          )
      }
    };
  });
